import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// The landing page ships in the main bundle; every other page is split out and loaded on demand,
// so marketing visitors don't download the dashboard.
import Home from '@/app/page';

const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'));

// Public & auth pages
const Login = lazy(() => import('@/app/login/page'));
const Signup = lazy(() => import('@/app/signup/page'));
const SignupSuccess = lazy(() => import('@/app/signup/success/page'));
const ForgotPassword = lazy(() => import('@/app/forgot-password/page'));
const Join = lazy(() => import('@/app/join/page'));
const Privacy = lazy(() => import('@/app/privacy/page'));
const Terms = lazy(() => import('@/app/terms/page'));
const Support = lazy(() => import('@/app/support/page'));
const DataDeletion = lazy(() => import('@/app/data-deletion/page'));
const Grant2026 = lazy(() => import('@/app/grant-2026/page'));
const Register = lazy(() => import('@/app/(public)/register/page'));

// Dashboard pages
const Dashboard = lazy(() => import('@/app/(dashboard)/dashboard/page'));
const Users = lazy(() => import('@/app/(dashboard)/users/page'));
const UserDetail = lazy(() => import('@/app/(dashboard)/users/detail/page'));
const UserDiet = lazy(() => import('@/app/(dashboard)/users/diet/page'));
const Gyms = lazy(() => import('@/app/(dashboard)/gyms/page'));
const Staff = lazy(() => import('@/app/(dashboard)/staff/page'));
const Trainers = lazy(() => import('@/app/(dashboard)/trainers/page'));
const Classes = lazy(() => import('@/app/(dashboard)/classes/page'));
const Plans = lazy(() => import('@/app/(dashboard)/plans/page'));
const Payments = lazy(() => import('@/app/(dashboard)/payments/page'));
const Billing = lazy(() => import('@/app/(dashboard)/billing/page'));
const Subscriptions = lazy(() => import('@/app/(dashboard)/subscriptions/page'));
const DietPlansV2 = lazy(() => import('@/app/(dashboard)/diet-plans-v2/page'));
const DietCalendar = lazy(() => import('@/app/(dashboard)/diet-calendar/page'));
const FoodLibrary = lazy(() => import('@/app/(dashboard)/food-library/page'));
const ExercisePlans = lazy(() => import('@/app/(dashboard)/exercise-plans/page'));
const Reviews = lazy(() => import('@/app/(dashboard)/reviews/page'));
const Promotions = lazy(() => import('@/app/(dashboard)/promotions/page'));
const Store = lazy(() => import('@/app/(dashboard)/store/page'));
const Chat = lazy(() => import('@/app/(dashboard)/chat/page'));
const CheckIn = lazy(() => import('@/app/(dashboard)/check-in/page'));
const Appointments = lazy(() => import('@/app/(dashboard)/appointments/page'));
const FinancialReports = lazy(() => import('@/app/(dashboard)/financial-reports/page'));
const Profile = lazy(() => import('@/app/(dashboard)/profile/page'));
const GymSettings = lazy(() => import('@/app/(dashboard)/gym-settings/page'));
const ClassBookings = lazy(() => import('@/app/(dashboard)/class-bookings/page'));
const Reports = lazy(() => import('@/app/(dashboard)/reports/page'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/success" element={<SignupSuccess />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/join" element={<Join />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/grant-2026" element={<Grant2026 />} />
      <Route path="/register" element={<Register />} />

      {/* Dashboard layout: pathless parent so child paths are /dashboard, /users, etc. */}
      <Route element={<DashboardLayout />}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="users/detail" element={<UserDetail />} />
        <Route path="users/diet" element={<UserDiet />} />
        <Route path="gyms" element={<Gyms />} />
        <Route path="staff" element={<Staff />} />
        <Route path="trainers" element={<Trainers />} />
        <Route path="classes" element={<Classes />} />
        <Route path="plans" element={<Plans />} />
        <Route path="payments" element={<Payments />} />
        <Route path="billing" element={<Billing />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="diet-plans" element={<Navigate to="/diet-plans-v2" replace />} />
        <Route path="diet-plans-v2" element={<DietPlansV2 />} />
        <Route path="diet-calendar" element={<DietCalendar />} />
        <Route path="food-library" element={<FoodLibrary />} />
        <Route path="exercise-plans" element={<ExercisePlans />} />
        <Route path="reviews" element={<Reviews />} />
        <Route path="promotions" element={<Promotions />} />
        <Route path="store" element={<Store />} />
        <Route path="chat" element={<Chat />} />
        <Route path="check-in" element={<CheckIn />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="financial-reports" element={<FinancialReports />} />
        <Route path="profile" element={<Profile />} />
        <Route path="gym-settings" element={<GymSettings />} />
        <Route path="class-bookings" element={<ClassBookings />} />
        <Route path="reports" element={<Reports />} />
        <Route path="cron" element={<Reports />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}
